import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Award,
  Brush,
  CalendarOff,
  ChartColumn,
  Clock,
  ExternalLink,
  Factory,
  FileCheck,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MonitorPlay,
  Package,
  PackagePlus,
  PackageX,
  PauseCircle,
  Settings,
  SlidersHorizontal,
  Smartphone,
  Truck,
  Users,
} from 'lucide-react'

import { LinkSuporte } from '@/components/link-suporte'
import { LogoOP, MarcaOP } from '@/components/marca'
import { ConviteInstalar } from '@/components/pwa/convite-instalar'
import { VersaoApp } from '@/components/pwa/versao-app'
import { Button } from '@/components/ui/button'
import { iniciais } from '@/lib/nomes'
import { lembrarArea } from '@/lib/pwa'
import { PERFIS_WEB, exigirSessao } from '@/lib/sessao'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao')({
  // Exige sessão web; operador logado no tablet não entra aqui.
  beforeLoad: async ({ context, location }) => ({
    me: await exigirSessao({ context, location, perfis: PERFIS_WEB }),
  }),
  component: LayoutGestao,
})

const MENU = [
  {
    grupo: 'Operação',
    itens: [
      { to: '/gestao', titulo: 'Visão geral', Icone: LayoutDashboard, exact: true },
      { to: '/gestao/pedidos', titulo: 'Pedidos', Icone: PackagePlus, exact: false },
      { to: '/gestao/indicadores', titulo: 'Indicadores', Icone: ChartColumn, exact: false },
    ],
  },
  {
    grupo: 'Desempenho',
    itens: [
      { to: '/gestao/bonus', titulo: 'Bônus', Icone: Award, exact: false },
      { to: '/gestao/fechamentos', titulo: 'Fechamentos', Icone: FileCheck, exact: false },
      { to: '/gestao/avaliacao', titulo: 'Avaliação', Icone: SlidersHorizontal, exact: false },
    ],
  },
  {
    grupo: 'Cadastros',
    itens: [
      { to: '/gestao/fabrica', titulo: 'Fábrica', Icone: Factory, exact: false },
      { to: '/gestao/produtos', titulo: 'Produtos', Icone: Package, exact: false },
      { to: '/gestao/motivos-pausa', titulo: 'Motivos de pausa', Icone: PauseCircle, exact: false },
      { to: '/gestao/motivos-perda', titulo: 'Motivos de perda', Icone: PackageX, exact: false },
      { to: '/gestao/atividades', titulo: 'Atividades sem ficha', Icone: Brush, exact: false },
      { to: '/gestao/origens', titulo: 'Origens de pedido', Icone: Truck, exact: false },
      { to: '/gestao/turnos', titulo: 'Turnos', Icone: Clock, exact: false },
      { to: '/gestao/ausencias', titulo: 'Ausências e feriados', Icone: CalendarOff, exact: false },
      { to: '/gestao/usuarios', titulo: 'Usuários', Icone: Users, exact: false },
      { to: '/gestao/configuracoes', titulo: 'Configurações', Icone: Settings, exact: false },
    ],
  },
] as const

const CLASSE_ITEM =
  'relative flex h-10 shrink-0 items-center justify-center gap-3 rounded-xl px-3 text-sm font-medium text-fg-muted transition-colors duration-100 hover:bg-elevated/60 hover:text-fg xl:justify-start'

/**
 * Casca da gestão: menu fixo na altura da tela (rola por dentro se preciso).
 * Abaixo de 1280 px vira um trilho de ícones para sobrar largura às tabelas.
 */
function LayoutGestao() {
  const { me } = Route.useRouteContext()
  const trpc = useTRPC()
  const config = useQuery(trpc.cadastros.configuracao.obter.queryOptions())
  useEffect(() => lembrarArea('/gestao'), [])
  return (
    <div className="flex min-h-full">
      <aside className="nao-imprimir sticky top-0 flex h-dvh w-18 shrink-0 flex-col gap-4 border-r border-border bg-surface py-4 xl:w-64 xl:px-4">
        <Link
          to="/gestao"
          aria-label="Projetive OP — visão geral"
          className="flex justify-center px-2 xl:justify-start"
        >
          <MarcaOP className="size-9 xl:hidden" />
          <span className="hidden xl:block">
            <LogoOP tamanho="size-9" />
          </span>
        </Link>
        <nav
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-3 xl:mt-4 xl:gap-5 xl:px-0"
          aria-label="Menu da gestão"
        >
          {MENU.map((grupo) => (
            <div key={grupo.grupo} className="flex flex-col gap-1">
              <p className="hidden px-3 text-[11px] font-semibold tracking-widest text-fg-subtle uppercase xl:block">
                {grupo.grupo}
              </p>
              <span aria-hidden className="mx-auto mb-1 h-px w-8 bg-border xl:hidden" />
              {grupo.itens.map(({ to, titulo, Icone, exact }) => (
                <Link
                  key={to}
                  to={to}
                  title={titulo}
                  activeOptions={{ exact }}
                  className={CLASSE_ITEM}
                  activeProps={{
                    className:
                      'bg-elevated text-fg before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-brand',
                  }}
                >
                  <Icone className="size-4 shrink-0" aria-hidden />
                  <span className="max-xl:sr-only">{titulo}</span>
                </Link>
              ))}
            </div>
          ))}
          {/* Trocar de área sem voltar ao início (no app instalado, o início reabre a última). */}
          <div className="flex flex-col gap-1">
            <p className="hidden px-3 text-[11px] font-semibold tracking-widest text-fg-subtle uppercase xl:block">
              Outras telas
            </p>
            <span aria-hidden className="mx-auto mb-1 h-px w-8 bg-border xl:hidden" />
            <Link to="/diretoria" title="Diretoria (celular)" className={CLASSE_ITEM}>
              <Smartphone className="size-4 shrink-0" aria-hidden />
              <span className="max-xl:sr-only">Diretoria (celular)</span>
            </Link>
            <a
              href="/painel"
              target="_blank"
              rel="noopener"
              title="Painel da TV (abre em nova aba)"
              className={CLASSE_ITEM}
            >
              <MonitorPlay className="size-4 shrink-0" aria-hidden />
              <span className="max-xl:sr-only">Painel da TV</span>
              <ExternalLink
                className="ml-auto size-3.5 shrink-0 opacity-60 max-xl:hidden"
                aria-hidden
              />
            </a>
          </div>
        </nav>
        <div className="flex shrink-0 flex-col gap-2 px-3 xl:px-0">
          <ConviteInstalar
            contexto="gestao"
            compacto
            className="max-xl:justify-center max-xl:px-0"
          />
          <LinkSuporte
            whatsapp={config.data?.whatsappSuporte}
            mensagem={`Olá! Preciso de ajuda na gestão do Projetive OP (${me.nome}).`}
            compacto="xl"
            className="px-0 xl:justify-start xl:px-3"
          />
          <Usuario nome={me.nome} />
          <VersaoApp className="self-start max-xl:hidden" />
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-6 xl:p-8 print:p-0">
        <Outlet />
      </main>
    </div>
  )
}

/** Quem está logado e o Sair; ao sair, nada da sessão fica em cache. */
function Usuario({ nome }: { nome: string }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const sair = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: async () => {
        queryClient.clear()
        await navigate({ to: '/login' })
      },
    }),
  )
  return (
    <div className="flex items-center gap-2 border-t border-border pt-3 max-xl:flex-col">
      <span
        aria-hidden
        title={nome}
        className="grid size-9 shrink-0 place-items-center rounded-full bg-brand/15 text-xs font-bold text-brand"
      >
        {iniciais(nome)}
      </span>
      <span className="hidden min-w-0 flex-1 truncate text-sm text-fg-muted xl:block">{nome}</span>
      <Link
        to="/minha-senha"
        search={{ voltar: '/gestao' }}
        aria-label="Trocar minha senha"
        title="Trocar minha senha"
        className="grid size-9 shrink-0 place-items-center rounded-xl text-fg-muted transition-colors hover:bg-elevated hover:text-fg"
      >
        <KeyRound className="size-4" aria-hidden />
      </Link>
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 px-2.5"
        onClick={() => sair.mutate()}
        carregando={sair.isPending}
        aria-label="Sair"
        title="Sair"
      >
        <LogOut className="size-4" aria-hidden />
      </Button>
    </div>
  )
}
